import { useState, useCallback, useEffect, useRef } from "react";
import { useRunAdminCommand } from "./useRunAdminCommand";
import { apiClient } from "../../utils/apiClient";

export interface WorkflowProgress {
  /** Human-readable description of current progress */
  progress: string;
  /** Numeric progress from 0 to 1 (0% to 100%) */
  progressPercent: number;
}

interface UseAdminWorkflowActionOptions {
  command: string;
  pollInterval?: number;
  onComplete?: () => void;
}

export interface UseAdminWorkflowActionResult {
  execute: (args: Record<string, any>) => Promise<void>;
  isStarting: boolean;
  isTracking: boolean;
  isComplete: boolean;
  progress: WorkflowProgress | null;
  commandResult: Record<string, any> | null;
  startError: string | null;
  reset: () => void;
}

export function useAdminWorkflowAction({
  command,
  pollInterval = 2000,
  onComplete,
}: UseAdminWorkflowActionOptions): UseAdminWorkflowActionResult {
  const { executeCommand } = useRunAdminCommand();

  const [isStarting, setIsStarting] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState<WorkflowProgress | null>(null);
  const [commandResult, setCommandResult] = useState<Record<string, any> | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recurringWorkflowIdRef = useRef<string | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollState = useCallback(async () => {
    const id = recurringWorkflowIdRef.current;
    if (!id) return;

    try {
      const data = await apiClient.get<{ state: WorkflowProgress; currentWorkflowId: string | null }>(
        `/api/workflows/recurring-workflows/${id}/state`,
      );

      if (data.state) {
        setProgress(data.state);

        if (data.state.progressPercent >= 1) {
          stopPolling();
          setIsTracking(false);
          setIsComplete(true);
          onComplete?.();
        } else if (data.currentWorkflowId === null) {
          stopPolling();
          setIsTracking(false);
          setStartError(data.state.progress || "Workflow failed");
        }
      }
    } catch (err) {
      // Silently ignore polling errors — the workflow may not have written state yet
      console.warn("[useAdminWorkflowAction] Polling error:", err);
    }
  }, [stopPolling, onComplete]);

  const startPolling = useCallback(() => {
    stopPolling();
    // Poll immediately, then on interval
    pollState();
    pollTimerRef.current = setInterval(pollState, pollInterval);
  }, [pollState, pollInterval, stopPolling]);

  const execute = useCallback(
    async (args: Record<string, any>) => {
      setIsStarting(true);
      setStartError(null);
      setProgress(null);
      setCommandResult(null);
      setIsComplete(false);
      setIsTracking(false);

      try {
        const response = await executeCommand(command, args);

        if (!response.success) {
          setStartError(response.error || "Command failed");
          setIsStarting(false);
          return;
        }

        setCommandResult(response.result ?? null);

        const rwId = response.result?.recurringWorkflowId;
        if (rwId) {
          recurringWorkflowIdRef.current = rwId;
          setIsStarting(false);
          setIsTracking(true);
          startPolling();
        } else {
          // Command succeeded but no workflow to track
          setIsStarting(false);
          setIsComplete(true);
        }
      } catch (err: any) {
        setStartError(err.message || "Unexpected error");
        setIsStarting(false);
      }
    },
    [command, executeCommand, startPolling],
  );

  const reset = useCallback(() => {
    stopPolling();
    recurringWorkflowIdRef.current = null;
    setIsStarting(false);
    setIsTracking(false);
    setIsComplete(false);
    setProgress(null);
    setCommandResult(null);
    setStartError(null);
  }, [stopPolling]);

  return {
    execute,
    isStarting,
    isTracking,
    isComplete,
    progress,
    commandResult,
    startError,
    reset,
  };
}
